import React, { useEffect, useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Search from '@mui/icons-material/Search';
import { useSelector } from 'react-redux';

import faqData from './pwa-faq.json';
import { useCleverTapPageTracking, trackAnalyticsEvent } from '../../utils/clevertap';

const PWAFAQ = () => {
  // CleverTap screen tracking
  useCleverTapPageTracking('FAQ_PWA', 'faq');
  const userData = useSelector(state => state.user?.userData);

  const categoriesList = Object.keys(faqData);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);

  // Track FAQ page view on load
  useEffect(() => {
    trackAnalyticsEvent('PWA_FAQ_PAGE_LOAD', {
      screen: 'pwa_faq',
      categories_count: categoriesList.length,
      timestamp: new Date().toISOString()
    });
  }, [categoriesList.length]);

  const handleSearchChange = (e) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    
    // Track search when user types (debounced)
    if (newSearchTerm.length >= 3) {
      trackAnalyticsEvent('PWA_FAQ_SEARCH', {
        search_term: newSearchTerm,
        search_length: newSearchTerm.length,
        screen: 'pwa_faq'
      });
    }
  };

  const handleCategoryToggle = (category) => {
    const newSelectedCategories = selectedCategories.includes(category)
      ? selectedCategories.filter((c) => c !== category)
      : [...selectedCategories, category];
    
    // Track filter chip click
    trackAnalyticsEvent('PWA_FAQ_FILTER_CHIP_CLICK', {
      manufacturer_id: userData?.manufacturer_id || '',
      filter_category: category,
      action: selectedCategories.includes(category) ? 'remove' : 'add',
      selected_filters: newSelectedCategories
    });
    
    setSelectedCategories(newSelectedCategories);
  };

  const handleFAQItemClick = (question, category) => {
    trackAnalyticsEvent('PWA_FAQ_ITEM_CLICK', {
      manufacturer_id: userData?.manufacturer_id || '',
      faq_question: question,
      faq_category: category,
      search_term: searchTerm || ''
    });
  };

  const filteredFaqs = useMemo(() => {
    let faqs = [];

    // If no category selected, show all FAQs from all categories
    if (selectedCategories.length === 0) {
      categoriesList.forEach((cat) => {
        faqs = faqs.concat(faqData[cat] || []);
      });
    } else {
      selectedCategories.forEach((cat) => {
        faqs = faqs.concat(faqData[cat] || []);
      });
    }

    // Apply search filter
    return faqs.filter((faq) =>
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [selectedCategories, searchTerm, categoriesList]);

  return (
    <Box sx={{ p: 2, mx: 'auto' }}>
      <TextField
        fullWidth
        placeholder='Search here...'
        value={searchTerm}
        onChange={handleSearchChange}
        sx={{ mb: 2 }}
        size='small'
        InputProps={{
          startAdornment: <Search sx={{ mr: 1 }} />,
          sx: { borderRadius: '20px' },
        }}
      />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        {categoriesList.map((categoryItem) => (
          <Button
            key={categoryItem}
            variant={
              selectedCategories.includes(categoryItem)
                ? 'contained'
                : 'outlined'
            }
            onClick={() => handleCategoryToggle(categoryItem)}
            size='small'
            sx={{
              borderRadius: '15px',
              textTransform: 'capitalize',
              '&:hover': { boxShadow: 'none' },
              px: 3,
            }}
          >
            {categoryItem}
          </Button>
        ))}
      </Box>
      {filteredFaqs.length > 0 ? (
        filteredFaqs.map((faq) => (
          <Accordion
            key={faq.id}
            onChange={(event, isExpanded) => {
              if (isExpanded) {
                handleFAQItemClick(faq.question, faq.category || 'general');
              }
            }}
            sx={{
              boxShadow: 'none',
              borderBottom: '1px solid #e0e0e0',
              borderRadius: 0,
              py: 1,
              '&:last-child': { borderBottom: 'none' },
              '&.Mui-expanded': { margin: 0 },
              '&::before': { display: 'none' },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                '& .MuiAccordionSummary-expandIconWrapper': { ml: 1 },
              }}
            >
              <Typography
                fontWeight='bold'
                variant='body1'
              >
                {faq.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                variant='body1'
                dangerouslySetInnerHTML={{ __html: faq.answer }}
                sx={{ whiteSpace: 'pre-line' }}
              />
            </AccordionDetails>
          </Accordion>
        ))
      ) : (
        <Typography
          align='center'
          color='text.secondary'
          mt={2}
        >
          No FAQs found.
        </Typography>
      )}
    </Box>
  );
};

export default PWAFAQ;
